import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  FormControl,
  InputGroup,
  Button,
  Spinner,
  Card,
  Table,
  Badge,
} from 'react-bootstrap';
import {
  Return,
  loadDeletedReturns as fetchDeletedReturnsPage,
} from '../../api/returns';
import {
  deleteReturnServer,
  restoreReturnServer,
} from '../../api/returnsServer';
import {
  Search as FiSearch,
  ArrowLeft as FiArrowLeft,
  Eye as FiEye,
  Trash3 as FiTrash2,
  ArrowCounterclockwise as FiRotateCcw,
} from 'react-bootstrap-icons';
import listStyles from '../shared/layout/dataListTable.module.scss';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import { useLoading } from '../../hooks/useLoading';
import ReturnView from './ReturnView';

function getApprovalStateInfo(state: string) {
  switch (state) {
    case 'pending':
      return { label: 'Pendiente', variant: 'warning' as const };
    case 'approved':
      return { label: 'Aprobado', variant: 'success' as const };
    case 'rejected':
      return { label: 'Rechazado', variant: 'danger' as const };
    default:
      return { label: 'Desconocido', variant: 'secondary' as const };
  }
}

function mapReturnDeleteError(error: any): string {
  const detail = String(error?.response?.data?.detail || error?.message || '').trim();
  const normalized = detail.toLowerCase();
  if (normalized.includes('client credit was already used or refunded')) {
    return 'No se puede eliminar la devolución porque el saldo a favor generado ya fue usado o reembolsado.';
  }
  if (normalized.includes('would make product inventory negative')) {
    return 'No se puede eliminar la devolución porque dejaría inventario negativo en uno o más productos.';
  }
  if (normalized.includes('cannot be restored')) {
    return 'No se puede restaurar una devolución aprobada eliminada porque sus efectos financieros ya fueron revertidos.';
  }
  return detail || 'Error desconocido';
}

function getReturnReference(returnObj: Return): string {
  if (returnObj.returnNumber) {
    return `#${returnObj.returnNumber}`;
  }
  if (returnObj.id) {
    return `${returnObj.id.substring(0, 8)}`;
  }
  return '#';
}

interface ReturnTrashBinProps {
  device: string;
  addToast: (message: string) => void;
  navStatus: string;
  organizationId: string;
  onBack: () => void;
}

export default function ReturnTrashBin({
  device,
  addToast,
  navStatus,
  organizationId,
  onBack,
}: ReturnTrashBinProps) {
  const { showConfirmation } = useConfirmationDialog();
  const { showLoading, hideLoading } = useLoading();
  const [returns, setReturns] = useState<Return[]>([]);
  const [loadMore, setLoadMore] = useState(false);
  const [lastDocument, setLastDocument] = useState<any>(null);
  const [loadMorePending, setLoadMorePending] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedReturn, setSelectedReturn] = useState<Return | null>(null);
  const [showReturnView, setShowReturnView] = useState(false);
  const mountedRef = useRef(true);

  // Memoized date formatting function
  const formatReturnDate = useCallback((created: any): string => {
    if (!created) return 'N/A';

    try {
      let date: Date;
      // Handle Firestore timestamp format
      if (created && typeof created === 'object' && 'seconds' in created) {
        // Firestore timestamp: { seconds: number, nanoseconds: number }
        date = new Date((created as any).seconds * 1000);
      } else if (created instanceof Date) {
        date = created;
      } else {
        date = new Date(created);
      }

      return isNaN(date.getTime())
        ? 'N/A'
        : date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
    } catch (e) {
      return 'N/A';
    }
  }, []);

  // Load deleted returns (where deleted = true)
  const loadDeletedReturns = useCallback(
    async (searchTerm?: string, lastDoc?: any) => {
      if (loadMorePending) return;

      setLoadMorePending(true);
      try {
        const result = await fetchDeletedReturnsPage(
          organizationId,
          searchTerm,
          lastDoc
        );

        if (mountedRef.current) {
          if (lastDoc) {
            setReturns(prev => [...prev, ...result.returns]);
          } else {
            setReturns(result.returns);
          }
          setLastDocument(result.lastDoc);
          setLoadMore(result.hasMore);
        }
      } catch (e) {
        console.error('Error loading deleted returns:', e);
        if (mountedRef.current) {
          addToast(
            'Error cargando devoluciones eliminadas: ' + (e as Error).message
          );
          setReturns([]);
        }
      } finally {
        if (mountedRef.current) {
          setLoadMorePending(false);
        }
      }
    },
    [organizationId, addToast, loadMorePending]
  );

  // Load more returns
  const handleLoadMore = useCallback(() => {
    if (loadMore && !loadMorePending && lastDocument) {
      loadDeletedReturns(search, lastDocument);
    }
  }, [loadMore, loadMorePending, lastDocument, loadDeletedReturns, search]);

  // Handle search
  const handleSearch = useCallback(() => {
    loadDeletedReturns(search);
  }, [loadDeletedReturns, search]);

  // Handle restore return
  const handleRestoreReturn = useCallback(
    async (returnObj: Return) => {
      if (!mountedRef.current) return;

      showLoading('Restaurando Devolución', 'overlay');

      try {
        await restoreReturnServer(organizationId, returnObj.id);

        hideLoading();
        addToast('Devolución restaurada exitosamente');

        // Refresh the deleted returns list
        loadDeletedReturns(search);
      } catch (e) {
        console.log(e);
        hideLoading();
        addToast(
          `Error restaurando la devolución: ${
            mapReturnDeleteError(e)
          }`
        );
      }
    },
    [
      organizationId,
      addToast,
      showLoading,
      hideLoading,
      loadDeletedReturns,
      search,
    ]
  );

  // Handle hard delete return
  const handleHardDeleteReturn = useCallback(
    async (returnObj: Return) => {
      if (!mountedRef.current) return;

      showLoading('Eliminando Permanentemente', 'overlay');

      try {
        await deleteReturnServer(organizationId, returnObj.id, false); // false = hard delete
        hideLoading();
        addToast('Devolución eliminada permanentemente');

        // Refresh the deleted returns list
        loadDeletedReturns(search);
      } catch (e) {
        console.log(e);
        hideLoading();
        addToast(`Error eliminando la devolución: ${mapReturnDeleteError(e)}`);
      }
    },
    [
      organizationId,
      addToast,
      showLoading,
      hideLoading,
      loadDeletedReturns,
      search,
    ]
  );

  // Handle view return
  const handleViewReturn = useCallback((returnObj: Return) => {
    setSelectedReturn(returnObj);
    setShowReturnView(true);
  }, []);

  // Handle close return view
  const handleCloseReturnView = useCallback(() => {
    setShowReturnView(false);
    setSelectedReturn(null);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadDeletedReturns();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <div className="d-flex align-items-center gap-3">
            <Button variant="outline-secondary" onClick={onBack}>
              <FiArrowLeft />
            </Button>
            <h4>Papelera de Devoluciones</h4>
            <Badge bg="secondary">
              {returns.length} devoluciones eliminadas
            </Badge>
          </div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <FormControl
              placeholder="Buscar devoluciones eliminadas..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
            <Button variant="outline-secondary" onClick={handleSearch}>
              <FiSearch />
            </Button>
          </InputGroup>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          {returns.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No hay devoluciones eliminadas</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>Devolución</th>
                    <th>Cliente</th>
                    <th>Vendedor</th>
                    <th>Total</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {returns.map(r => {
                    const stateInfo = getApprovalStateInfo(r.approvalState);
                    return (
                      <tr
                        key={r.id}
                        className={listStyles.clickableRow}
                        onClick={() => handleViewReturn(r)}
                      >
                        <td>{getReturnReference(r)}</td>
                        <td>{r.clientName}</td>
                        <td>{r.creatorSalesPerson}</td>
                        <td>${r.total}</td>
                        <td>
                          <Badge bg={stateInfo.variant}>
                            {stateInfo.label}
                          </Badge>
                        </td>
                        <td>{formatReturnDate(r.created)}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewReturn(r)}
                              title="Ver detalles"
                            >
                              <FiEye />
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              disabled={r.approvalState === 'approved'}
                              onClick={() =>
                                showConfirmation(
                                  {
                                    title: 'Restaurar Devolución',
                                    message:
                                      r.approvalState === 'approved'
                                        ? 'Esta devolución fue aprobada antes de eliminarse. Sus efectos financieros ya fueron revertidos, por lo que no puede restaurarse de forma segura. Crea una nueva devolución si necesitas registrar nuevamente la operación.'
                                        : '¿Estás seguro de que quieres restaurar esta devolución?',
                                    confirmText: 'Restaurar',
                                    cancelText: 'Cancelar',
                                  },
                                  () => handleRestoreReturn(r)
                                )
                              }
                              title={
                                r.approvalState === 'approved'
                                  ? 'No se puede restaurar una devolución aprobada eliminada'
                                  : 'Restaurar devolución'
                              }
                            >
                              <FiRotateCcw />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() =>
                                showConfirmation(
                                  {
                                    title: 'Eliminar Permanentemente',
                                    message:
                                      r.approvalState === 'approved'
                                        ? '¿Estás seguro de que quieres eliminar esta devolución permanentemente? Esta acción no se puede deshacer. La reversión financiera e inventario ya debieron aplicarse cuando se movió a papelera; esta acción solo elimina el registro histórico visible.'
                                        : '¿Estás seguro de que quieres eliminar esta devolución permanentemente? Esta acción no se puede deshacer.',
                                    confirmText: 'Eliminar Permanentemente',
                                    cancelText: 'Cancelar',
                                  },
                                  () => handleHardDeleteReturn(r)
                                )
                              }
                              title="Eliminar permanentemente"
                            >
                              <FiTrash2 />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {loadMore === true ? (
                    <tr>
                      <td colSpan={7} className="text-center">
                        <Button
                          variant="outline-primary"
                          onClick={handleLoadMore}
                          disabled={loadMorePending}
                        >
                          {loadMorePending ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Cargando...
                            </>
                          ) : (
                            'Cargar Más'
                          )}
                        </Button>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {showReturnView && selectedReturn && (
        <ReturnView
          show={showReturnView}
          onClose={handleCloseReturnView}
          returnObj={selectedReturn}
        />
      )}
    </Container>
  );
}
